'use client'

import { createTheme, ThemeProvider as MuiTheme } from '@mui/material/styles'
import { FC, PropsWithChildren } from 'react'

const theme = createTheme({
  cssVariables: true,

  typography: {
    fontFamily: 'var(--font-family-sans)',

    allVariants: {
      fontFamily: 'var(--font-family-sans)',
    },

    h1: {
      fontWeight: 700,
      fontSize: 'clamp(2rem, 2rem + 2.4vw, 2.2rem)',
      letterSpacing: '-0.05em',
    },

    h2: {
      fontWeight: 700,
      fontSize: 'clamp(1.8rem, 1.8rem + 2vw, 2rem)',
      letterSpacing: '-0.04em',
    },

    h3: {
      fontWeight: 700,
      fontSize: 'clamp(1.6rem, 1.6rem + 1.6vw, 1.6rem)',
      letterSpacing: '-0.03em',
    },

    h4: {
      fontWeight: 700,
      fontSize: 'clamp(1.4rem, 1.4rem + 1.2vw, 1.4rem)',
      letterSpacing: '-0.02em',
    },

    h5: {
      fontWeight: 700,
      fontSize: 'clamp(1.2rem, 1.2rem + 1vw, 1.2rem)',
      letterSpacing: '-0.01em',
    },

    h6: {
      fontWeight: 600,
      fontSize: 'clamp(1rem, 1rem + 0.8vw, 1.2rem)',
    },

    body1: {
      fontSize: '1rem',
    },

    body2: {
      fontSize: '0.9rem',
    },

    subtitle1: {
      fontSize: 'clamp(1rem, 0.8rem + 0.4vw, 1.4rem)',
    },

    subtitle2: {
      fontSize: 'clamp(0.8rem, 0.8rem + 0.2vw, 1.2rem)',
    },

    caption: {
      fontSize: 'clamp(0.6rem, 0.6rem + 0.2vw, 0.8rem)',
    },
  },
  shadows: [
    'none',
    '0 1px 4px 0 rgba(0, 0, 0, 0.3), 0 1px 2px 0 rgba(0, 0, 0, 0.2)',
    '0 1px 5px 0 rgba(0, 0, 0, 0.35), 0 1px 2px 0 rgba(0, 0, 0, 0.25)',
    '0 1px 7px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    '0 1px 9px 0 rgba(0, 0, 0, 0.45), 0 1px 2px 0 rgba(0, 0, 0, 0.35)',
    '0 1px 11px 0 rgba(0, 0, 0, 0.5), 0 1px 2px 0 rgba(0, 0, 0, 0.4)',
    '0 1px 13px 0 rgba(0, 0, 0, 0.55), 0 1px 2px 0 rgba(0, 0, 0, 0.45)',
    '0 1px 15px 0 rgba(0, 0, 0, 0.6), 0 1px 2px 0 rgba(0, 0, 0, 0.5)',
    '0 1px 17px 0 rgba(0, 0, 0, 0.65), 0 1px 2px 0 rgba(0, 0, 0, 0.55)',
    '0 1px 19px 0 rgba(0, 0, 0, 0.7), 0 1px 2px 0 rgba(0, 0, 0, 0.6)',
    '0 1px 21px 0 rgba(0, 0, 0, 0.75), 0 1px 2px 0 rgba(0, 0, 0, 0.65)',
    '0 1px 23px 0 rgba(0, 0, 0, 0.8), 0 1px 2px 0 rgba(0, 0, 0, 0.7)',
    '0 1px 25px 0 rgba(0, 0, 0, 0.85), 0 1px 2px 0 rgba(0, 0, 0, 0.75)',
    '0 1px 27px 0 rgba(0, 0, 0, 0.9), 0 1px 2px 0 rgba(0, 0, 0, 0.8)',
    '0 1px 29px 0 rgba(0, 0, 0, 0.95), 0 1px 2px 0 rgba(0, 0, 0, 0.85)',
    '0 1px 31px 0 rgba(0, 0, 0, 1), 0 1px 2px 0 rgba(0, 0, 0, 0.9)',
    '0 1px 33px 0 rgba(0, 0, 0, 1), 0 1px 2px 0 rgba(0, 0, 0, 0.95)',
    '0 1px 35px 0 rgba(0, 0, 0, 1), 0 1px 2px 0 rgba(0, 0, 0, 1)',
    '0 1px 37px 0 rgba(0, 0, 0, 1), 0 1px 2px 0 rgba(0, 0, 0, 1)',
    '0 1px 39px 0 rgba(0, 0, 0, 1), 0 1px 2px 0 rgba(0, 0, 0, 1)',
    '0 1px 41px 0 rgba(0, 0, 0, 1), 0 1px 2px 0 rgba(0, 0, 0, 1)',
    '0 1px 43px 0 rgba(0, 0, 0, 1), 0 1px 2px 0 rgba(0, 0, 0, 1)',
    '0 1px 45px 0 rgba(0, 0, 0, 1), 0 1px 2px 0 rgba(0, 0, 0, 1)',
    '0 1px 47px 0 rgba(0, 0, 0, 1), 0 1px 2px 0 rgba(0, 0, 0, 1)',
    '0 1px 49px 0 rgba(0, 0, 0, 1), 0 1px 2px 0 rgba(0, 0, 0, 1)',
  ],

  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },

  palette: {
    mode: 'dark',

    common: {
      black: '#131313',
      white: '#f0f0f1',
    },

    action: {
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },

    primary: {
      light: '#2ed3ed',
      main: '#27B1C6',
      dark: '#1e8899',
    },

    secondary: {
      light: '#6df6c1',
      main: '#59CDA1',
      dark: '#459f7c',
    },

    background: {
      default: '#131313',
      paper: '#1C1C1E',
    },

    text: {
      primary: '#f0f0f1',
      secondary: 'rgba(255, 255, 255, 0.7)',
      disabled: 'rgba(255, 255, 255, 0.3)',
    },

    success: {
      main: '#59CDA1',
    },

    error: {
      main: '#ef4444',
    },

    divider: '#2a2a2a',
  },

  components: {
    MuiTypography: {
      styleOverrides: {
        h3: {
          '@media (max-width: 900px)': {
            fontSize: '1.3rem',
          },
        },
        h4: {
          '@media (max-width: 900px)': {
            fontSize: '1.15rem',
          },
        },
        h5: {
          '@media (max-width: 900px)': {
            fontSize: '1rem',
          },
        },
      },
      variants: [
        {
          props: { color: 'gradient' },
          style: {
            background: `linear-gradient(90deg, #27B1C6 0%, #59CDA1 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundSize: '200% 100%',
            backgroundPosition: '50% 0',
            fontWeight: 700,
          },
        },
      ],
    },

    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 12,
          textTransform: 'none',
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #27B1C6 0%, #59CDA1 100%)',
          color: 'white',
          boxShadow: '0 2px 8px rgba(39, 177, 198, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #2ed3ed 0%, #6df6c1 100%)',
            boxShadow: '0 4px 12px rgba(39, 177, 198, 0.4)',
          },
          '&.Mui-disabled': {
            background: '#1a1a1a',
            color: 'rgba(255, 255, 255, 0.3)',
          },
        },
      },
      variants: [
        {
          props: { color: 'gradient', variant: 'contained' },
          style: {
            background: 'linear-gradient(135deg, #27B1C6 0%, #59CDA1 100%)',
            border: 'none',
            color: 'white',
            boxShadow: '0 2px 8px rgba(39, 177, 198, 0.3)',

            '&:hover': {
              background: 'linear-gradient(135deg, #2ed3ed 0%, #6df6c1 100%)',
              boxShadow: '0 4px 12px rgba(39, 177, 198, 0.4)',
            },
          },
        },

        {
          props: { color: 'black', variant: 'contained' },
          style: {
            background: '#1C1C1E',
            border: '1px solid #2a2a2a',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            padding: '10px 28px',
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              background: '#252527',
            },
          },
        },
      ],
    },

    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#1C1C1E',
          borderRadius: 12,
          border: '1px solid #2a2a2a',
        },
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #2a2a2a',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#1C1C1E',
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',

          '&.MuiOutlinedInput-root': {
            backgroundColor: '#161618',
            border: '1px solid #2a2a2a',
            position: 'relative',
            borderRadius: 8,

            // Multiline fields size themselves from their row count, so pinning
            // a height would clip the textarea instead of growing with it.
            '&:not(.MuiInputBase-multiline)': {
              height: 42,
            },

            input: {
              padding: '10px 12px',
              color: '#f0f0f1',
              fontSize: '0.875rem',
              height: 'auto',

              '&::placeholder': {
                color: '#8E8E93',
                opacity: 1,
              },
            },

            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },

            '&:hover': {
              backgroundColor: '#1a1a1c',
              borderColor: '#3a3a3a',
            },

            '&.Mui-focused': {
              backgroundColor: '#161618',
              borderColor: '#27B1C6',
            },
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#8E8E93',
          fontSize: '0.875rem',
          transform: 'translate(12px, 12px) scale(1)',
          '&.Mui-focused': {
            color: '#27B1C6',
          },
          '&.MuiInputLabel-shrink': {
            backgroundColor: '#161618',
            borderRadius: '4px',
            padding: '0 4px',
            transform: 'translate(12px, -9px) scale(0.85)',
          },
        },
        outlined: {
          '&.MuiInputLabel-shrink': {
            backgroundColor: '#161618',
            borderRadius: '4px',
            padding: '0 4px',
          },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          marginTop: 4,
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)': {
            height: 42,
          },
          '& .MuiFormHelperText-root': {
            color: '#8E8E93',
            fontSize: '0.75rem',
            '&.Mui-error': {
              color: '#ef4444',
            },
          },
        },
      },
    },

    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          height: 42,
          '&.MuiOutlinedInput-root': {
            height: 42,
          },
        },
        select: {
          padding: '10px 12px !important',
          minHeight: 'unset !important',
          height: 'auto',
          display: 'flex',
          alignItems: 'center',
        },
      },
    },

    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#2a2a2a',
          borderBottom: 'none',
          color: '#f0f0f1',
          '&.MuiTableCell-body, &.MuiTableCell-head': {
            border: 'none',
          },
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#2a2a2a',
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        standardError: {
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          color: '#ef4444',
        },
        standardSuccess: {
          backgroundColor: 'rgba(89, 205, 161, 0.1)',
          color: '#59CDA1',
        },
      },
    },

    MuiLink: {
      styleOverrides: {
        root: {
          color: '#27B1C6',
          textDecoration: 'none',
          '&:hover': {
            color: '#2ed3ed',
          },
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1C1C1E',
          border: '1px solid #2a2a2a',
          borderRadius: 12,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#f0f0f1',
          fontSize: '0.875rem',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        },
      },
    },

    MuiStepper: {
      styleOverrides: {
        root: {
          '& .MuiStepConnector-line': {
            borderColor: '#2a2a2a',
          },
        },
      },
    },

    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: 'rgba(255, 255, 255, 0.5)',
          '&.Mui-active': {
            color: '#f0f0f1',
          },
          '&.Mui-completed': {
            color: '#59CDA1',
          },
        },
      },
    },

    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: '#2a2a2a',
          '&.Mui-active': {
            color: '#27B1C6',
          },
          '&.Mui-completed': {
            color: '#59CDA1',
          },
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1C1C1E',
          borderRight: '1px solid #2a2a2a',
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1C1C1E',
          border: '1px solid #2a2a2a',
        },
        option: {
          color: '#f0f0f1',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
          '&[aria-selected="true"]': {
            backgroundColor: 'rgba(39, 177, 198, 0.2)',
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:not(.MuiInputBase-multiline)': {
            height: 42,
          },
          '& input[type="date"]': {
            colorScheme: 'dark',
          },
        },
        input: {
          padding: '10px 12px',
          fontSize: '0.875rem',
          height: 'auto',
          boxSizing: 'border-box',
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '16px',
          '@media (max-width: 600px)': {
            padding: '12px',
          },
          '&:last-child': {
            paddingBottom: '16px',
            '@media (max-width: 600px)': {
              paddingBottom: '12px',
            },
          },
        },
      },
    },

    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            paddingLeft: '12px',
            paddingRight: '12px',
          },
        },
      },
    },
  },
})

const Theme: FC<PropsWithChildren> = ({ children }) => {
  return <MuiTheme theme={theme}>{children}</MuiTheme>
}

export default Theme
export { theme }
